import { supabase } from './supabase';
import type { AuctionChargeStatus } from './types';

/**
 * Admin-only Sales Room payment helpers. Reads go through Supabase (admin RLS
 * lets staff read all auction_charges / member_payment_methods); the charge +
 * refund actions hit Cloudflare Pages Functions that re-verify the caller's
 * role and own all money movement server-side.
 */

export interface SaleroomChargeRow {
  post_id: string;
  lot_number: string;
  lot_name: string;
  post_title: string | null;
  post_slug: string | null;
  ends_at: string;
  /** Total to charge: ledger amount when a charge exists, else hammer + flat postage. */
  amount_cents: number;
  /** Flat postage in cents (null = unspecified, 0 = free postage). */
  shipping_cost: number | null;
  winner_id: string;
  winner_name: string | null;
  winner_handle: string | null;
  winner_has_card: boolean;
  /** Ledger status, or null when no charge has been started yet. */
  charge_status: AuctionChargeStatus | null;
  charge_id: string | null;
  last_error: string | null;
  created_at: string | null;
  /** Postage leg actually charged (067) + where it came from; null pre-charge. */
  postage_cents: number | null;
  postage_source: 'live_rate' | 'flat' | null;
  postage_service: string | null;
  /** Shipping label, once bought via the admin "Buy label" action. */
  label_url: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  /** How the charge was raised: auto on close, Buy Now checkout, or admin. */
  charge_origin: 'admin' | 'auto_close' | 'buy_now' | null;
  /** Latest Shippo tracking status + delivery time (081), for the order stage. */
  tracking_status: string | null;
  delivered_at: string | null;
  /** Buyer's 48h window to fix a failed/SCA charge (083); null otherwise. */
  action_deadline: string | null;
}

/**
 * The fulfilment pipeline stage of an order, derived from the charge status +
 * label + tracking. This is what the Orders board filters and sorts by.
 *   needs_action  - no charge yet / failed / pending_review (team must act)
 *   in_progress   - charging / processing / waiting on buyer SCA
 *   ready_to_ship - paid, no label bought yet  (the team's main queue)
 *   shipped       - label bought / in transit
 *   delivered     - tracking says delivered
 *   expired       - buyer's 48h window lapsed; forfeited (team decides) (083)
 *   refunded      - returned
 */
export type FulfillmentStage =
  | 'needs_action' | 'in_progress' | 'ready_to_ship' | 'shipped' | 'delivered'
  | 'expired' | 'refunded';

export function fulfillmentStage(row: SaleroomChargeRow): FulfillmentStage {
  switch (row.charge_status) {
    case 'refunded':
      return 'refunded';
    case 'expired':
      return 'expired';
    case 'succeeded':
      if (row.delivered_at || row.tracking_status === 'DELIVERED') return 'delivered';
      if (row.label_url) return 'shipped';
      return 'ready_to_ship';
    case 'pending':
    case 'processing':
    case 'requires_action':
      return 'in_progress';
    default: // null, pending_review, failed, canceled
      return 'needs_action';
  }
}

/** Per-stage counts across all orders (for the Orders filter chips + Dashboard). */
export function stageCounts(rows: SaleroomChargeRow[]): Record<FulfillmentStage, number> {
  const out: Record<FulfillmentStage, number> = {
    needs_action: 0, in_progress: 0, ready_to_ship: 0, shipped: 0, delivered: 0,
    expired: 0, refunded: 0,
  };
  for (const r of rows) out[fulfillmentStage(r)]++;
  return out;
}

async function authedFetch(path: string, init?: RequestInit) {
  const { data: sess } = await supabase.auth.getSession();
  const token = sess.session?.access_token;
  if (!token) throw new Error('Sign in first.');
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}`, ...(init?.headers ?? {}) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any)?.error ?? `Request failed (${res.status}).`);
  return data as any;
}

/**
 * Every ended auction that has a winner, joined to its charge ledger row (if
 * any), the winner's identity, and whether they still hold a card. Drives the
 * admin "confirm winner & charge" dashboard.
 */
export async function fetchSaleroomCharges(): Promise<SaleroomChargeRow[]> {
  const { data: auctions, error } = await supabase
    .from('post_auctions_with_state')
    .select('post_id, lot_number, lot_name, ends_at, current_high_amount, current_high_bidder_id, shipping_cost')
    .eq('state', 'ended')
    .not('current_high_bidder_id', 'is', null)
    .order('ends_at', { ascending: false });
  if (error) throw error;

  const rows = (auctions ?? []).filter((a: any) => a.current_high_amount > 0);
  if (rows.length === 0) return [];

  const postIds = rows.map((a: any) => a.post_id);
  const winnerIds = Array.from(new Set(rows.map((a: any) => a.current_high_bidder_id as string)));

  const [postsRes, winnersRes, chargesRes, cardsRes] = await Promise.all([
    supabase.from('posts').select('id, title, slug').in('id', postIds),
    supabase.from('profiles').select('id, display_name, handle').in('id', winnerIds),
    supabase.from('auction_charges')
      .select('id, post_id, status, amount_cents, last_error, created_at, postage_cents, postage_source, postage_service, label_url, tracking_number, tracking_url, charge_origin, tracking_status, delivered_at, action_deadline')
      .in('post_id', postIds)
      .order('created_at', { ascending: false }),
    supabase.from('member_payment_methods')
      .select('user_id, has_card_on_file')
      .in('user_id', winnerIds),
  ]);
  if (postsRes.error) throw postsRes.error;
  if (winnersRes.error) throw winnersRes.error;
  if (chargesRes.error) throw chargesRes.error;
  if (cardsRes.error) throw cardsRes.error;

  const posts = new Map((postsRes.data ?? []).map((p: any) => [p.id, p]));
  const winners = new Map((winnersRes.data ?? []).map((p: any) => [p.id, p]));
  const cards = new Map((cardsRes.data ?? []).map((c: any) => [c.user_id, !!c.has_card_on_file]));
  // Most-recent charge per post (the query is ordered desc, so first wins).
  const charges = new Map<string, any>();
  for (const c of chargesRes.data ?? []) if (!charges.has(c.post_id)) charges.set(c.post_id, c);

  return rows.map((a: any): SaleroomChargeRow => {
    const post = posts.get(a.post_id);
    const winner = winners.get(a.current_high_bidder_id);
    const charge = charges.get(a.post_id);
    return {
      post_id: a.post_id,
      lot_number: a.lot_number,
      lot_name: a.lot_name,
      post_title: post?.title ?? null,
      post_slug: post?.slug ?? null,
      ends_at: a.ends_at,
      // The ledger is the truth for what was actually captured; before any
      // charge exists, mirror the server's charge math (hammer + flat postage).
      amount_cents: charge?.amount_cents ?? (a.current_high_amount + (a.shipping_cost ?? 0)),
      shipping_cost: a.shipping_cost ?? null,
      winner_id: a.current_high_bidder_id,
      winner_name: winner?.display_name ?? null,
      winner_handle: winner?.handle ?? null,
      winner_has_card: cards.get(a.current_high_bidder_id) ?? false,
      charge_status: (charge?.status as AuctionChargeStatus) ?? null,
      charge_id: charge?.id ?? null,
      last_error: charge?.last_error ?? null,
      created_at: charge?.created_at ?? null,
      postage_cents: charge?.postage_cents ?? null,
      postage_source: charge?.postage_source ?? null,
      postage_service: charge?.postage_service ?? null,
      label_url: charge?.label_url ?? null,
      tracking_number: charge?.tracking_number ?? null,
      tracking_url: charge?.tracking_url ?? null,
      charge_origin: charge?.charge_origin ?? null,
      tracking_status: charge?.tracking_status ?? null,
      delivered_at: charge?.delivered_at ?? null,
      action_deadline: charge?.action_deadline ?? null,
    };
  });
}

/**
 * The four working buckets the admin Saleroom dashboard tabs by:
 *   • action - still needs the team to charge or retry (incl. no charge yet)
 *   • progress - in flight: charging, processing, or waiting on member SCA
 *   • paid - captured
 *   • refunded - returned
 */
export type SaleroomChargeGroup = 'action' | 'progress' | 'paid' | 'refunded';

export function saleroomChargeGroup(status: AuctionChargeStatus | null): SaleroomChargeGroup {
  switch (status) {
    case 'succeeded':
      return 'paid';
    case 'refunded':
      return 'refunded';
    case 'pending':
    case 'processing':
    case 'requires_action':
      return 'progress';
    default:
      // null (no charge started), pending_review, failed, canceled
      return 'action';
  }
}

/** Count of charges still needing team action - drives the nav-tab indicator. */
export function countOutstandingSaleroomCharges(rows: SaleroomChargeRow[]): number {
  return rows.reduce((n, r) => n + (saleroomChargeGroup(r.charge_status) === 'action' ? 1 : 0), 0);
}

/** Confirm the winner and fire the off-session charge. */
export async function chargeAuctionWinner(postId: string): Promise<{ ok: boolean; status: string; error?: string }> {
  return authedFetch(`/api/admin/auctions/${postId}/charge`, { method: 'POST', body: '{}' });
}

/** Pre-charge breakdown (067): the exact hammer + postage + total the charge
 *  endpoint will use, with the postage source and destination. Read-only. */
export interface PostagePreview {
  hammer_cents: number;
  postage_cents: number;
  postage_source: 'live_rate' | 'flat' | 'none';
  postage_service: string | null;
  total_cents: number;
  destination: string | null;
  has_address: boolean;
}

export async function fetchPostagePreview(postId: string): Promise<PostagePreview> {
  return authedFetch(`/api/admin/auctions/${postId}/postage`, { method: 'POST', body: '{}' });
}

/** Buy the shipping label for a PAID lot (idempotent server-side). */
export async function buyAuctionLabel(postId: string): Promise<{
  ok: boolean;
  label_url: string;
  tracking_number: string | null;
  tracking_url: string | null;
}> {
  return authedFetch(`/api/admin/auctions/${postId}/label`, { method: 'POST', body: '{}' });
}

/** Refund a succeeded auction-win charge. */
export async function refundAuctionCharge(postId: string): Promise<{ ok: boolean; status: string }> {
  return authedFetch(`/api/admin/auctions/${postId}/refund`, { method: 'POST', body: '{}' });
}

// ---------- Ship-from (dispatch) address config (077) --------------------

export interface ShipFromConfig {
  ship_from_name: string | null;
  ship_from_street1: string | null;
  ship_from_street2: string | null;
  ship_from_city: string | null;
  ship_from_state: string | null;
  ship_from_zip: string | null;
  ship_from_country: string | null;
  ship_from_phone: string | null;
  ship_from_email: string | null;
}

/** Current ship-from address + whether a Shippo token is configured at all. */
export async function fetchShipFromConfig(): Promise<{
  config: ShipFromConfig | null;
  token_configured: boolean;
}> {
  return authedFetch('/api/admin/shipping/config', { method: 'GET' });
}

/** Save the ship-from address (upserts the single shipping_config row). */
export async function saveShipFromConfig(input: ShipFromConfig): Promise<{
  ok: boolean;
  config: ShipFromConfig;
  token_configured: boolean;
}> {
  return authedFetch('/api/admin/shipping/config', { method: 'PUT', body: JSON.stringify(input) });
}

// ---------- Live-lot bid management (admin) ------------------------------
// Admins can void an individual bid on a live lot; the lot then falls back to
// the next-highest remaining bid (post_auctions_with_state recomputes it).

export interface AdminLotBid {
  id: string;
  amount_cents: number;
  created_at: string;
  bidder_name: string | null;
  bidder_handle: string | null;
}

export interface AdminLiveLot {
  post_id: string;
  lot_number: string;
  lot_name: string;
  post_slug: string | null;
  ends_at: string;
  current_high_amount: number;
  bids: AdminLotBid[];   // highest first
}

/** Every currently-live lot with its full bid list (highest first), for the
 *  admin bid-management view. */
export async function fetchLiveLotsWithBids(): Promise<AdminLiveLot[]> {
  const { data: auctions, error } = await supabase
    .from('post_auctions_with_state')
    .select('post_id, lot_number, lot_name, ends_at, current_high_amount')
    .eq('state', 'live')
    .order('ends_at', { ascending: true });
  if (error) throw error;

  const lots = auctions ?? [];
  if (lots.length === 0) return [];
  const postIds = lots.map((a: any) => a.post_id);

  const [postsRes, bidsRes] = await Promise.all([
    supabase.from('posts').select('id, slug').in('id', postIds),
    supabase
      .from('auction_bids_public')
      .select('id, post_id, amount, created_at, bidder:profiles!auction_bids_bidder_id_fkey ( display_name, handle )')
      .in('post_id', postIds)
      .order('amount', { ascending: false }),
  ]);
  if (postsRes.error) throw postsRes.error;
  if (bidsRes.error) throw bidsRes.error;

  const slugs = new Map((postsRes.data ?? []).map((p: any) => [p.id, p.slug]));
  const bidsByPost = new Map<string, AdminLotBid[]>();
  for (const b of (bidsRes.data ?? []) as any[]) {
    const arr = bidsByPost.get(b.post_id) ?? [];
    arr.push({
      id: b.id,
      amount_cents: b.amount,
      created_at: b.created_at,
      bidder_name: b.bidder?.display_name ?? null,
      bidder_handle: b.bidder?.handle ?? null,
    });
    bidsByPost.set(b.post_id, arr);
  }

  return lots.map((a: any): AdminLiveLot => ({
    post_id: a.post_id,
    lot_number: a.lot_number,
    lot_name: a.lot_name,
    post_slug: slugs.get(a.post_id) ?? null,
    ends_at: a.ends_at,
    current_high_amount: a.current_high_amount,
    bids: bidsByPost.get(a.post_id) ?? [],
  }));
}

/** Void one bid on a live lot (admin/developer only). Throws on RPC error. */
export async function adminRemoveBid(bidId: string): Promise<void> {
  const { error } = await supabase.rpc('admin_remove_bid', { target_bid_id: bidId });
  if (error) throw error;
}

/**
 * Relist a forfeited / unsold lot as a fresh auction closing at `endsAtISO`
 * (085/087). Clears the old bids + charge history and reopens the lot. Refuses
 * if the lot was already paid. Admin/developer only.
 */
export async function relistAuction(postId: string, endsAtISO: string): Promise<void> {
  const { error } = await supabase.rpc('relist_auction', { target_post_id: postId, new_ends_at: endsAtISO });
  if (error) throw error;
}
